import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Search } from 'lucide-react';

import { LoadingSpinner, InlineLoadingState } from '../../components/LoadingSpinner';
import type { Organization } from '../../types';

type OrgHit = Pick<Organization, 'id' | 'name'>;

function popoverBelowAnchor(anchor: DOMRect, minWidth: number): { left: number; top: number; width: number; maxHeight: number } {
  const margin = 8;
  const gap = 4;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(Math.max(anchor.width, minWidth), vw - 2 * margin);
  const left = Math.min(Math.max(anchor.left, margin), vw - width - margin);
  /** Immer unter dem Feld öffnen — nie nach oben über den Anker schieben. */
  const top = anchor.bottom + gap;
  const maxHeight = Math.max(120, vh - margin - top);
  return { left, top, width, maxHeight };
}

export function OrganizationAssignSelect({
  valueId,
  valueLabel,
  onSelect,
  disabled,
  saving,
  panelZIndex = 280,
}: {
  valueId: string | null;
  valueLabel: string;
  onSelect: (org: OrgHit) => void | Promise<void>;
  disabled?: boolean;
  saving?: boolean;
  panelZIndex?: number;
}) {
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState<{ left: number; top: number; width: number; maxHeight: number } | null>(null);
  const [q, setQ] = React.useState('');
  const [debounced, setDebounced] = React.useState('');
  const [hits, setHits] = React.useState<OrgHit[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q.trim()), 200);
    return () => clearTimeout(t);
  }, [q]);

  const updatePosition = React.useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos(popoverBelowAnchor(rect, 280));
  }, []);

  React.useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const qs = new URLSearchParams();
        if (debounced) qs.set('q', debounced);
        const res = await fetch(`/api/organizations?${qs.toString()}`);
        const data = res.ok ? ((await res.json()) as Organization[]) : [];
        if (!cancelled) {
          setHits(
            Array.isArray(data)
              ? data.map((o) => ({ id: String(o.id), name: String(o.name ?? '') }))
              : [],
          );
        }
      } catch {
        if (!cancelled) setHits([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, debounced]);

  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  React.useEffect(() => {
    if (!open) setQ('');
  }, [open]);

  const busy = saving === true || disabled === true;

  const pick = async (org: OrgHit) => {
    if (String(org.id) === String(valueId ?? '')) {
      setOpen(false);
      return;
    }
    await onSelect(org);
    setOpen(false);
  };

  const panel =
    open &&
    !busy &&
    pos &&
    createPortal(
      <div
        ref={panelRef}
        className="z-[280] grid grid-rows-[auto_minmax(0,1fr)] overflow-hidden rounded-lg border border-app-border bg-app-surface shadow-xl"
        style={{
          position: 'fixed',
          left: pos.left,
          top: pos.top,
          width: pos.width,
          maxHeight: pos.maxHeight,
          zIndex: panelZIndex,
        }}
      >
        <div className="p-2 border-b border-app-border/60">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Organisation suchen …"
              className="w-full rounded-md border border-app-border py-1.5 pl-8 pr-2 text-xs outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400/30"
              autoFocus
            />
          </div>
        </div>
        <div className="min-h-0 overflow-y-auto overflow-x-hidden">
          {loading ? (
            <InlineLoadingState label="Lädt …" className="py-6 text-xs" />
          ) : hits.length === 0 ? (
            <div className="px-3 py-4 text-xs text-app-text-subtle text-center">Keine Treffer.</div>
          ) : (
            hits.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => void pick(o)}
                className={`block w-full truncate px-3 py-2 text-left text-xs hover:bg-app-hover ${
                  String(o.id) === String(valueId ?? '') ? 'bg-blue-50 text-blue-900 font-medium' : 'text-app-text'
                }`}
              >
                {o.name}
              </button>
            ))
          )}
        </div>
      </div>,
      document.body,
    );

  return (
    <div ref={anchorRef} className="min-w-0 w-full">
      <button
        type="button"
        disabled={busy}
        onClick={() => {
          if (busy) return;
          setOpen((v) => !v);
        }}
        className="w-full flex items-center justify-between gap-2 rounded-md border border-app-border bg-app-surface px-2.5 py-1.5 text-left text-sm text-app-text hover:bg-app-hover disabled:opacity-50"
      >
        <span className="truncate min-w-0">{valueLabel.trim() || 'Organisation wählen …'}</span>
        {busy ? (
          <LoadingSpinner className="shrink-0 text-gray-400" />
        ) : (
          <ChevronDown className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden />
        )}
      </button>
      {panel}
    </div>
  );
}
